import { Input } from "@nextui-org/react";
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const MarkdownWithInputs = ({
  markdownContent,
  procedureData,
  onChangeProcedureData,
  errorMessage,
}) => {
  const handlebarRegex = /{{([^}]+)}}/g;

  // Function to decode HTML entities
  const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  // Function to convert HTML to clean markdown content
  const convertHtmlToMarkdown = (html) => {
    if (!html) return "";
    
    // Decode HTML entities first
    let content = decodeHtml(html);
    
    // First, let's check if this is already markdown content
    if (!content.includes('<') && !content.includes('>')) {
      return content; // Already markdown
    }
    
    // Replace HTML tags with markdown equivalents
    content = content
      // Handle paragraphs - remove p tags but keep content
      .replace(/<p[^>]*>(.*?)<\/p>/g, '$1\n\n')
      // Handle line breaks
      .replace(/<br\s*\/?>/g, '\n')
      // Handle strong/bold tags
      .replace(/<strong[^>]*>(.*?)<\/strong>/g, '**$1**')
      .replace(/<b[^>]*>(.*?)<\/b>/g, '**$1**')
      // Handle emphasis/italic tags
      .replace(/<em[^>]*>(.*?)<\/em>/g, '*$1*')
      .replace(/<i[^>]*>(.*?)<\/i>/g, '*$1*')
      // Handle headings
      .replace(/<h1[^>]*>(.*?)<\/h1>/g, '# $1\n\n')
      .replace(/<h2[^>]*>(.*?)<\/h2>/g, '## $1\n\n')
      .replace(/<h3[^>]*>(.*?)<\/h3>/g, '### $1\n\n')
      .replace(/<h4[^>]*>(.*?)<\/h4>/g, '#### $1\n\n')
      .replace(/<h5[^>]*>(.*?)<\/h5>/g, '##### $1\n\n')
      .replace(/<h6[^>]*>(.*?)<\/h6>/g, '###### $1\n\n')
      // Handle blockquotes
      .replace(/<blockquote[^>]*>(.*?)<\/blockquote>/g, '> $1\n\n')
      // Handle code blocks
      .replace(/<code[^>]*>(.*?)<\/code>/g, '`$1`')
      .replace(/<pre[^>]*>(.*?)<\/pre>/g, '```\n$1\n```\n\n')
      // Handle links
      .replace(/<a\s+href="([^"]*)"[^>]*>(.*?)<\/a>/g, '[$2]($1)')
      // Handle lists - unordered
      .replace(/<ul[^>]*>(.*?)<\/ul>/g, (match, content) => {
        return content.replace(/<li[^>]*>(.*?)<\/li>/g, '- $1\n') + '\n';
      })
      // Handle lists - ordered
      .replace(/<ol[^>]*>(.*?)<\/ol>/g, (match, content) => {
        let counter = 1;
        return content.replace(/<li[^>]*>(.*?)<\/li>/g, () => `${counter++}. $1\n`) + '\n';
      })
      // Handle individual list items (fallback)
      .replace(/<li[^>]*>(.*?)<\/li>/g, '- $1\n')
      // Handle tables
      .replace(/<table[^>]*>(.*?)<\/table>/g, (match, content) => {
        // Simple table conversion
        return content
          .replace(/<thead[^>]*>(.*?)<\/thead>/g, '$1')
          .replace(/<tbody[^>]*>(.*?)<\/tbody>/g, '$1')
          .replace(/<tr[^>]*>(.*?)<\/tr>/g, (trMatch, trContent) => {
            return trContent
              .replace(/<th[^>]*>(.*?)<\/th>/g, '| $1 ')
              .replace(/<td[^>]*>(.*?)<\/td>/g, '| $1 ')
              + '|\n';
          });
      })
      // Clean up any remaining HTML tags
      .replace(/<[^>]*>/g, '')
      // Clean up multiple newlines and spaces
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      .replace(/\n\s*\n/g, '\n\n')
      .trim();
    
    return content;
  };

  // Process the content to handle both HTML and markdown
  const processedContent = convertHtmlToMarkdown(markdownContent);

  // Custom component to handle handlebar replacements
  const CustomText = ({ children }) => {
    // If children is not a string, return it as is
    if (typeof children !== 'string') {
      return children;
    }

    const parts = [];
    let lastIndex = 0;
    let match;
    const regex = new RegExp(handlebarRegex);

    while ((match = regex.exec(children)) !== null) {
      // Push the text before the handlebar
      if (match.index > lastIndex) {
        parts.push(children.substring(lastIndex, match.index));
      }
      
      // Push the handlebar content as an input field
      const fieldName = match[1].trim();
      
      parts.push(
        <Input
          key={match.index}
          size="sm"
          type="text"
          variant="underlined"
          name={fieldName}
          placeholder={fieldName}
          className="w-[175px] inline-block mx-2"
          value={procedureData[fieldName] || ""}
          onChange={(event) => {
            onChangeProcedureData({
              ...procedureData,
              [fieldName]: event.target.value,
            });
          }}
          isInvalid={errorMessage[fieldName] !== undefined && errorMessage[fieldName] !== ""}
          errorMessage={errorMessage[fieldName]}
        />
      );

      lastIndex = regex.lastIndex;
    }

    // Push any remaining text after the last handlebar
    if (lastIndex < children.length) {
      parts.push(children.substring(lastIndex));
    }

    return parts.length > 0 ? parts : children;
  };

  return (
    <div className="markdown-content prose max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, children }) => {
            // Check if children contains any React elements (like Input components)
            const hasReactElements = React.Children.toArray(children).some(child => 
              React.isValidElement(child)
            );
            
            if (hasReactElements) {
              return (
                <p className="leading-8 mt-2 mb-4">
                  {children}
                </p>
              );
            }
            
            // If it's just text, process it with CustomText
            return (
              <p className="leading-8 mt-2 mb-4">
                <CustomText>{children}</CustomText>
              </p>
            );
          },
          ul: ({ node, children }) => (
            <ul className="list-disc list-inside leading-8 mt-2 mb-4">
              {children}
            </ul>
          ),
          ol: ({ node, children }) => (
            <ol className="list-decimal list-inside leading-8 mt-2 mb-4">
              {children}
            </ol>
          ),
          li: ({ node, children }) => {
            // Check if children contains any React elements
            const hasReactElements = React.Children.toArray(children).some(child => 
              React.isValidElement(child)
            );
            
            if (hasReactElements) {
              return (
                <li className="leading-8 mt-1">
                  {children}
                </li>
              );
            }
            
            return (
              <li className="leading-8 mt-1">
                <CustomText>{children}</CustomText>
              </li>
            );
          },
          h1: ({ node, children }) => (
            <h1 className="text-2xl font-bold leading-8 mt-6 mb-4">
              <CustomText>{children}</CustomText>
            </h1>
          ),
          h2: ({ node, children }) => (
            <h2 className="text-xl font-bold leading-8 mt-5 mb-3">
              <CustomText>{children}</CustomText>
            </h2>
          ),
          h3: ({ node, children }) => (
            <h3 className="text-lg font-bold leading-8 mt-4 mb-3">
              <CustomText>{children}</CustomText>
            </h3>
          ),
          h4: ({ node, children }) => (
            <h4 className="text-base font-bold leading-8 mt-4 mb-2">
              <CustomText>{children}</CustomText>
            </h4>
          ),
          h5: ({ node, children }) => (
            <h5 className="text-sm font-bold leading-8 mt-3 mb-2">
              <CustomText>{children}</CustomText>
            </h5>
          ),
          h6: ({ node, children }) => (
            <h6 className="text-xs font-bold leading-8 mt-3 mb-2">
              <CustomText>{children}</CustomText>
            </h6>
          ),
          blockquote: ({ node, children }) => (
            <blockquote className="border-l-4 border-gray-400 pl-4 italic my-4">
              <CustomText>{children}</CustomText>
            </blockquote>
          ),
          a: ({ node, children, ...props }) => (
            <a className="text-emerald-500 underline" {...props}>
              <CustomText>{children}</CustomText>
            </a>
          ),
          table: ({ node, children }) => (
            <table className="w-full text-left border-collapse my-4">
              {children}
            </table>
          ),
          thead: ({ node, children }) => (
            <thead className="bg-gray-200">{children}</thead>
          ),
          tbody: ({ node, children }) => <tbody>{children}</tbody>,
          tr: ({ node, children }) => (
            <tr className="border-b">{children}</tr>
          ),
          th: ({ node, children }) => (
            <th className="py-2 px-4 font-semibold">
              <CustomText>{children}</CustomText>
            </th>
          ),
          td: ({ node, children }) => (
            <td className="py-2 px-4">
              <CustomText>{children}</CustomText>
            </td>
          ),
          strong: ({ node, children }) => (
            <strong className="font-semibold">
              <CustomText>{children}</CustomText>
            </strong>
          ),
          em: ({ node, children }) => (
            <em className="italic">
              <CustomText>{children}</CustomText>
            </em>
          ),
          code: ({ node, children }) => (
            <code className="bg-gray-100 p-1 rounded">
              <CustomText>{children}</CustomText>
            </code>
          ),
          pre: ({ node, children }) => (
            <pre className="bg-gray-100 p-4 rounded overflow-auto my-4">
              <CustomText>{children}</CustomText>
            </pre>
          ),
          hr: ({ node }) => (
            <hr className="border-t border-gray-300 my-6" />
          ),
          br: ({ node }) => <br />,
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownWithInputs;
